import React, { useContext } from 'react'
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PersonIcon from '@mui/icons-material/Person'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import WheelchairPickupIcon from '@mui/icons-material/WheelchairPickup'
import moment from 'moment'
import { BookingContext } from './NewBooking'
import { GlobalContext } from '../../../App'
import { regionLabels } from '../../../helpers/region'
import ExistingJourneys from './ExistingJourneys'

const SelectedPatientCard = ({ patient, onEdit, onRemove, portalMode = false }) => {
  const { existingBooking } = useContext(BookingContext)
  const { tenantInfo } = useContext(GlobalContext)
  const theme = useTheme()
  const d = patient?.demographics || {}

  const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY') : '—')

  return (
    <Stack gap={1}>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafc 100%)',
          overflow: 'hidden',
          border: 'none',
        }}
      >
        <CardContent>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 2 }}
          >
            {/* Patient Info */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 56,
                  height: 56,
                  fontSize: '1.5rem',
                }}
              >
                {d.firstName?.[0]}
                {d.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {d.firstName} {d.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {d.gender || '—'} • {formatDate(d.dob)}
                </Typography>
              </Box>
            </Stack>

            {/* NHS / GP / Actions */}
            <Stack spacing={0.5} textAlign="right" alignItems="flex-end">
              {patient.patientID && (
                <Typography variant="body2">
                  <strong>Patient ID:</strong> {patient.patientID}
                </Typography>
              )}
              {d.nhsNumber && (
                <Typography variant="body2">
                  <strong>
                    {regionLabels(tenantInfo?.region).nationalIdLabel}:
                  </strong>{' '}
                  {d.nhsNumber}
                </Typography>
              )}
              {d.gpSurgery && (
                <Typography variant="body2">
                  <strong>GP:</strong> {d.gpSurgery}
                </Typography>
              )}
              {!existingBooking && (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {onEdit && (
                    <Tooltip title="Edit patient">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={onEdit}
                        sx={{ border: '1px solid #d0d7de', bgcolor: 'white' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove patient">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={onRemove}
                      sx={{ border: '1px solid #f5c6cb', bgcolor: 'white' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Address & Contacts */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack spacing={0.5}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <LocationOnIcon fontSize="small" />
                  Address
                </Typography>
                <Typography variant="body2">
                  {[d.addressLine1, d.addressLine2, d.town, d.city, d.postCode]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={0.5}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <PersonIcon fontSize="small" />
                  Next of Kin
                </Typography>
                <Typography variant="body2">
                  {d.nokName
                    ? `${d.nokName} (${d.nokRelationship || '—'}) — ${
                        d.nokContactNumber || '—'
                      }`
                    : '—'}
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Clinical */}
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              <LocalHospitalIcon fontSize="small" />
              Clinical Information
            </Typography>
            <Typography variant="body2" color="text.primary">
              {patient.clinicalNotes || 'No clinical notes provided.'}
            </Typography>
          </Stack>

          {/* Mobility */}
          {patient.mobility && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={0.5}
              >
                <WheelchairPickupIcon fontSize="small" />
                Mobility
              </Typography>
              {patient.mobility.category ? (
                <Typography variant="body2">
                  <strong>Category:</strong> {patient.mobility.category}
                </Typography>
              ) : (
                <Chip
                  icon={<WarningAmberIcon />}
                  label="Mobility category not set — confirm before dispatching a vehicle"
                  color="warning"
                  size="small"
                  variant="filled"
                  sx={{ mt: 0.5 }}
                />
              )}
              {(patient.mobility.heightCm || patient.mobility.weightKg) && (
                <Typography variant="body2">
                  {patient.mobility.heightCm && (
                    <>
                      <strong>Height:</strong> {patient.mobility.heightCm} cm
                    </>
                  )}
                  {patient.mobility.heightCm && patient.mobility.weightKg && ' · '}
                  {patient.mobility.weightKg && (
                    <>
                      <strong>Weight:</strong> {patient.mobility.weightKg} kg
                    </>
                  )}
                </Typography>
              )}
              {patient.mobility.aids?.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
                  {patient.mobility.aids.map((aid, i) => (
                    <Chip
                      key={i}
                      label={aid}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        borderColor: theme.palette.primary.light,
                        color: theme.palette.primary.main,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* Risk Flags */}
          {patient.riskFlags?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={0.5}
              >
                <WarningAmberIcon fontSize="small" />
                Risk Flags
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
                {patient.riskFlags.map((flag, i) => (
                  <Chip
                    key={i}
                    label={flag}
                    color="error"
                    size="small"
                    variant="filled"
                    sx={{
                      fontWeight: 500,
                      borderRadius: 1,
                      backgroundColor: '#fdecea',
                      color: '#b71c1c',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
      {!portalMode && <ExistingJourneys patientID={patient?._id} openInNewTab />}
    </Stack>
  )
}

export default SelectedPatientCard
